import CommonTitle from '@/components/title/CommonTitle';
import { fonts } from '@/theme/fonts';
import styled from 'styled-components';
import Pagination from '@/components/pagination/Pagination';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import LicenseIssueModal from './modal/LicenseIssueModal';
import { useLoading } from '@/context/LoadingContenxt';
import { getLicenseKeyList } from '@/api/license-list/getLicenseKeyList';
import { useDispatch } from 'react-redux';
import { license } from '@/stores/slices/license-list/licenseSlice';
import { useAppSelector } from '@/stores/hooks';
import LicenseKeyListItem from './LicenseKeyListItem';
import LicenseDashboard from './LicenseDashboard';
import { useTranslation } from 'react-i18next';

const SIZE = 15;
const LicenseKeyManagementPage = () => {
  const [isOpenIssueModal, setIsOpenIssueModal] = useState<boolean>(false);
  const [isDeleteMode, setIsDeleteMode] = useState<boolean>(false);
  const { startLoading, stopLoading } = useLoading();
  const dispatch = useDispatch();
  const { licenseList } = useAppSelector((state) => state.license);
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page') ?? 1);
  const size = Number(searchParams.get('size') ?? SIZE);

  /** 페이지 변경 */
  const handlePageChange = (page: number) => {
    setSearchParams({
      page: String(page),
      size: String(size),
    });
  };

  const onClickDeleteButton = useCallback(() => {
    setIsDeleteMode((prev) => !prev);
  }, []);

  const fetchLicenseKeyList = useCallback(async () => {
    startLoading();
    try {
      const res = await getLicenseKeyList({
        page: currentPage,
        size: size,
      });
      if (res.isSuccess) {
        dispatch(license.setLicenseList(res.data));
      }
    } finally {
      stopLoading();
    }
  }, [currentPage, dispatch, size, startLoading, stopLoading]);

  useEffect(() => {
    fetchLicenseKeyList();
  }, [fetchLicenseKeyList]);

  if (!licenseList) return;

  return (
    <Container>
      {isOpenIssueModal && (
        <LicenseIssueModal
          open={isOpenIssueModal}
          onClose={() => setIsOpenIssueModal(false)}
          fetchList={fetchLicenseKeyList}
        />
      )}
      <CommonTitle title={t('licenseManagement.title')} />
      <LicenseDashboard />
      <TopActionArea>
        <PrimaryButton onClick={() => setIsOpenIssueModal(true)}>
          + {t('licenseManagement.issueLicenseKey')}
        </PrimaryButton>
        <GrayButton
          onClick={onClickDeleteButton}
          data-deletemode={isDeleteMode}
        >
          {isDeleteMode
            ? t('licenseManagement.disableRevocationMode')
            : t('licenseManagement.enableRevocationMode')}
        </GrayButton>
      </TopActionArea>
      <LicenseKeyListItem
        items={licenseList.items}
        isDeleteMode={isDeleteMode}
      />
      <PaginationWrap>
        <Pagination
          totalItems={licenseList.totalElements}
          itemsPerPage={SIZE}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </PaginationWrap>
    </Container>
  );
};

export default LicenseKeyManagementPage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 24px;
`;

const TopActionArea = styled.div`
  display: flex;
  justify-content: flex-end;
  font-weight: ${fonts.weight.medium};
  gap: 12px;
`;

const PrimaryButton = styled.button`
  min-width: 160px;
  height: 40px;
  padding: 0 20px;
  border-radius: 6px;
  background: #2563eb;
  color: #fff;
  font-size: 14px;
  font-weight: ${fonts.weight.semiBold};
  &:hover {
    background: #1d4ed8;
  }
`;

const GrayButton = styled(PrimaryButton)`
  background: #fff;
  color: #374151;
  border: 1px solid #d1d5db;

  &:hover {
    background: #f9fafb;
  }

  &[data-deletemode='true'] {
    background: #ef4444;
    border-color: #ef4444;
    color: #fff;

    &:hover {
      background: #dc2626;
    }
  }
`;

const PaginationWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`;
